import React, {useState} from 'react';
import { 
  UserPlusIcon, 
  UsersIcon,
  CalendarIcon,
  CogIcon,
  ChartBarIcon,
  HomeIcon,
  Bars3Icon,
  KeyIcon,
  CheckBadgeIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  GiftIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  CurrencyPoundIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import ContactSupportModal from './ContactSupportModal';

const TailwindSidebar = ({ isOpen, toggleSidebar, isAdmin, userName, hasPlans, venueFeatures = null }) => {

    // null  = features not yet loaded → show everything (avoids flash of hidden items)
    // []    = loaded, PT Directory venue → hide all gated items
    // [...] = loaded, Full Venue → show items whose feature is in the list
    const hasFeature = (f) => venueFeatures === null || venueFeatures.includes(f);
    const location = useLocation();
    const [supportModalOpen, setSupportModalOpen] = useState(false);
    

    const [openGroups, setOpenGroups] = useState({
        myActivity: true,
        myPayments: false,
        sessions: true,
        finance: false,
        reports: false,
        settings: false,
        ptDirectory: false,
    });

    const toggleGroup = (key) => {
        setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const isCurrentRoute = (path) => {
        const cleanPath = path.split('?')[0];
        return location.pathname === `/dashboard${cleanPath}`;
    };

    const isGroupActive = (items) => {
        return items.some(item => {
            if (item.requiresPlans && !hasPlans) return false;
            const cleanPath = item.path.split('?')[0];
            return location.pathname === `/dashboard${cleanPath}`;
        });
    };

    // Navigation structure: standalone items and collapsible groups
    const navGroups = [
        {
            type: 'item',
            name: 'Dashboard',
            path: '/',
            icon: HomeIcon,
            adminOnly: false,
        },
        // ── User sections ──────────────────────────────────────
        {
            type: 'group',
            key: 'myActivity',
            label: 'My Activity',
            adminOnly: false,
            icon: CalendarIcon,
            items: [
                { name: 'My Sessions', path: '/sessions?selected=mysessions', icon: CalendarIcon },
                { name: 'Classes', path: '/classes', icon: UsersIcon },
                { name: 'My Plans', path: '/my-plans', icon: ClipboardDocumentListIcon, requiresPlans: true },
            ],
        },
        {
            type: 'group',
            key: 'myPayments',
            label: 'My Payments',
            adminOnly: false,
            icon: CreditCardIcon,
            items: [
                { name: 'Buy Tokens', path: '/buy-tokens', icon: ShoppingCartIcon, requiresFeature: 'finance' },
                { name: 'Transaction History', path: '/transaction-history', icon: BanknotesIcon, requiresFeature: 'finance' },
                { name: 'Payment History', path: '/manage-subscription', icon: CreditCardIcon },
            ],
        },
        // ── Admin sections ─────────────────────────────────────
        {
            type: 'divider',
            adminOnly: true,
        },
        {
            type: 'group',
            key: 'sessions',
            label: 'Sessions',
            adminOnly: true,
            icon: CalendarDaysIcon,
            items: [
                { name: 'Admin Sessions', path: '/adminSession', icon: ChartBarIcon, requiresFeature: 'sessions' },
                { name: 'Admin Classes', path: '/adminClasses', icon: UserPlusIcon, requiresFeature: 'classes' },
                { name: 'Bulk Session Mgmt', path: '/bulk-session-management', icon: CalendarDaysIcon, requiresFeature: 'bulk_manage' },
                { name: 'Bulk User Actions', path: '/bulk-users', icon: UsersIcon },
            ],
        },
        {
            type: 'group',
            key: 'finance',
            label: 'Finance',
            adminOnly: true,
            requiresFeature: 'finance',
            icon: CurrencyPoundIcon,
            items: [
                { name: 'Token Settings', path: '/token-settings', icon: CurrencyPoundIcon },
                { name: 'Subscriptions', path: '/subscriptions', icon: KeyIcon },
                { name: 'Subscription Offers', path: '/subscription-offers', icon: GiftIcon },
                { name: 'My Subscription', path: '/subscription', icon: CheckBadgeIcon },
                { name: 'Manage Plans', path: '/plans', icon: ClipboardDocumentListIcon },
            ],
        },
        {
            type: 'item',
            name: 'Reports',
            path: '/reports',
            icon: DocumentChartBarIcon,
            adminOnly: true,
            requiresFeature: 'financial_reporting',
        },
        {
            type: 'group',
            key: 'settings',
            label: 'Settings',
            adminOnly: true,
            icon: CogIcon,
            items: [
                { name: 'Venue Settings', path: '/venue-settings', icon: MapPinIcon },
                { name: 'Membership', path: '/membership', icon: CogIcon, requiresFeature: 'finance'  },
                { name: 'Invite Link', path: '/adminTermsAndConditions', icon: DocumentTextIcon, requiresFeature: 'sing_up' },
                { name: 'Terms & Conditions', path: '/adminTermsAndConditions', icon: DocumentTextIcon, requiresFeature: 'finance'  },
                { name: 'Integrations', path: '/paymentproviderlinks', icon: KeyIcon, requiresFeature: 'finance'  },
            ],
        },
        {
            type: 'group',
            key: 'ptDirectory',
            label: 'PT Directory',
            adminOnly: true,
            icon: BuildingStorefrontIcon,
            items: [
                { name: 'PT Enquiries', path: '/pt-enquiries', icon: EnvelopeIcon },
                { name: 'Directory Listing', path: '/venue-listing', icon: BuildingStorefrontIcon },
            ],
        },
        // ── Bottom standalone ──────────────────────────────────
        {
            type: 'divider',
            adminOnly: false,
        },
        {
            type: 'item',
            name: 'Profile',
            path: '/profile',
            icon: UserPlusIcon,
            adminOnly: false,
        },
    ];

    const handleNavClick = () => {
        if (window.innerWidth < 768) toggleSidebar();
    };

    const renderItem = (item, isSubItem = false) => {
        if (item.requiresPlans && !hasPlans) return null;
        if (item.requiresFeature && !hasFeature(item.requiresFeature)) return null;

        const isActive = isCurrentRoute(item.path);
        const Icon = item.icon;

        return (
            <Link
                key={item.path}
                to={`/dashboard${item.path}`}
                onClick={handleNavClick}
                className={`flex items-center px-3 py-2 mb-0.5 rounded-lg transition-all duration-200 ${
                    isSubItem && isOpen ? 'pl-9' : ''
                } ${
                    isActive
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
            >
                <Icon className={`h-5 w-5 flex-shrink-0 ${isOpen ? 'mr-3' : 'mx-auto'}`} />
                {isOpen && <span className="font-medium text-sm">{item.name}</span>}
            </Link>
        );
    };

    const renderGroup = (group) => {
        // Hide the entire group if it requires a feature the venue doesn't have
        if (group.requiresFeature && !hasFeature(group.requiresFeature)) return null;

        const visibleItems = group.items.filter(item =>
            !(item.requiresPlans && !hasPlans) &&
            !(item.requiresFeature && !hasFeature(item.requiresFeature))
        );
        if (visibleItems.length === 0) return null;

        const isExpanded = openGroups[group.key];
        const GroupIcon = group.icon;
        const groupHasActive = isGroupActive(group.items);

        // Collapsed sidebar: show flat icons for all items in the group
        if (!isOpen) {
            return visibleItems.map(item => renderItem(item, false));
        }

        const groupId = `sidebar-group-${group.key}`;

        return (
            <div key={group.key} className="mb-0.5">
                <button
                    id={groupId}
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={isExpanded}
                    aria-controls={`${groupId}-content`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                        groupHasActive && !isExpanded
                            ? 'bg-primary-500/20 text-primary-300'
                            : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                    }`}
                >
                    <div className="flex items-center">
                        <GroupIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                        <span className="font-medium text-sm">{group.label}</span>
                    </div>
                    <ChevronDownIcon
                        className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </button>

                {isExpanded && (
                    <div
                        id={`${groupId}-content`}
                        role="region"
                        aria-labelledby={groupId}
                        className="mt-0.5"
                    >
                        {visibleItems.map(item => renderItem(item, true))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`fixed left-0 top-0 h-full bg-dark-900 border-r border-dark-700 transition-all duration-300 z-50 flex flex-col ${
            isOpen ? 'w-64' : 'w-16'
        }`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-dark-700 flex-shrink-0">
                {isOpen && (
                    <h2 className="text-xl font-bold text-white">Dashboard</h2>
                )}
                <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-lg hover:bg-dark-700 text-gray-400 hover:text-white transition-colors"
                >
                    <Bars3Icon className="h-6 w-6" />
                </button>
            </div>

            {/* Navigation – scrollable */}
            <nav className="flex-1 overflow-y-auto mt-2 px-2 pb-4">
                {navGroups.map((navItem, idx) => {
                    if (navItem.adminOnly && !isAdmin) return null;
                    if (navItem.requiresFeature && !hasFeature(navItem.requiresFeature)) return null;

                    if (navItem.type === 'divider') {
                        return isOpen ? (
                            <div key={`divider-${idx}`} className="my-2 border-t border-dark-700" />
                        ) : (
                            <div key={`divider-${idx}`} className="my-1 border-t border-dark-700 mx-2" />
                        );
                    }

                    if (navItem.type === 'item') {
                        return renderItem(navItem);
                    }

                    return renderGroup(navItem);
                })}
            </nav>

            {/* User info at bottom */}
            {isOpen && (
                <div className="flex-shrink-0 border-t border-dark-700 p-3">
                    {/* Contact Us button */}
                    <button
                        onClick={() => setSupportModalOpen(true)}
                        className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-gray-400 hover:bg-dark-700 hover:text-white transition-colors text-sm font-medium"
                    >
                        <QuestionMarkCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Contact Us</span>
                    </button>
                    <div className="bg-dark-800 rounded-lg p-3">
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0" aria-label={userName ? `${userName} avatar` : 'User avatar'}>
                                <span className="text-sm font-medium text-white" aria-hidden="true">{userName ? userName[0] : '?'}</span>
                            </div>
                            <div className="ml-3 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{userName}</p>
                                <p className="text-xs text-gray-400">
                                    {isAdmin ? 'Administrator' : 'Member'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Us button when sidebar is collapsed */}
            {!isOpen && (
                <div className="flex-shrink-0 border-t border-dark-700 py-3 flex justify-center">
                    <button
                        onClick={() => setSupportModalOpen(true)}
                        className="p-2 rounded-lg text-gray-400 hover:bg-dark-700 hover:text-white transition-colors"
                        title="Contact Us"
                    >
                        <QuestionMarkCircleIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            <ContactSupportModal
                isOpen={supportModalOpen}
                onClose={() => setSupportModalOpen(false)}
                userType={isAdmin ? 'Venue Admin' : 'Member'}
            />
        </div>
    );
};

export default TailwindSidebar;